/**
 * 司機綁定頁面
 * 第一次使用 LIFF 時，需要綁定司機帳號
 */

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLiff } from '@/contexts/LiffContext'
import { bindDriverAccount } from '@/services/driverService'
import { fetchBindCompanies, type BindCompanyOption } from '@/services/driverAuthService'
import { getStoredAccessToken } from '@/services/driverAuthService'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  MenuItem,
} from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import LockIcon from '@mui/icons-material/Lock'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'

const BindingPage: React.FC = () => {
  const navigate = useNavigate()
  const { lineUser } = useLiff()

  // ========== State ==========
  const [driverId, setDriverId] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [showCode, setShowCode] = useState(false)
  const [loading, setLoading] = useState(false)
  // 跨公司同代號：預查回多家時讓司機選擇所屬公司（司機編號只在同公司內唯一）
  const [companyOptions, setCompanyOptions] = useState<BindCompanyOption[]>([])
  const [selectedCompany, setSelectedCompany] = useState('')
  const [error, setError] = useState<string | null>(null)

  // ========== 檢查是否有系統 JWT ==========
  useEffect(() => {
    const token = getStoredAccessToken()
    if (!token) {
      // 沒有系統 JWT，需要先登入
      setError('請先進行 LINE 登入')
      console.warn('[BindingPage] 沒有系統 JWT，請先完成 LINE 登入')
    }
  }, [])

  // ========== Event Handlers ==========
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // 驗證欄位
    if (!driverId.trim()) {
      setError('請輸入司機編號')
      return
    }

    if (!verificationCode.trim()) {
      setError('請輸入驗證碼')
      return
    }

    if (!lineUser) {
      setError('LINE 使用者資料尚未載入')
      return
    }

    // 檢查是否有系統 JWT（在 lineLogin 時已儲存）
    const systemToken = getStoredAccessToken()
    if (!systemToken) {
      setError('認證已過期，請重新開啟應用')
      return
    }

    setLoading(true)

    try {
      // 預查：司機編號跨公司可重複；多家且尚未選擇 → 先讓司機選所屬公司
      const companies = await fetchBindCompanies(driverId.trim())
      if (companies.length > 1 && !selectedCompany) {
        setCompanyOptions(companies)
        setError('此司機編號存在於多家公司，請先選擇您的所屬公司')
        setLoading(false)
        return
      }
      const companyCode = companies.length > 1 ? selectedCompany : undefined

      // 透過 Service 層綁定（使用儲存的系統 JWT）
      const result = await bindDriverAccount(
        driverId.trim(),
        verificationCode,
        lineUser.userId,
        companyCode
      )

      if (result.success && result.driver) {
        // 儲存綁定資料到 localStorage
        localStorage.setItem(
          'driver-binding',
          JSON.stringify({
            lineUserId: lineUser.userId,
            driverId: result.driver.driverId,
            driverName: result.driver.name,
            companyName: result.driver.companyName,
            bindingDate: new Date().toISOString(),
          })
        )

        // 導向首頁
        navigate('/home', { replace: true })
      } else {
        setError(result.message)
      }
    } catch (err) {
      console.error('綁定失敗:', err)
      setError('綁定失敗，請稍後再試')
    } finally {
      setLoading(false)
    }
  }

  const handleToggleCode = () => {
    setShowCode(!showCode)
  }

  // ========== Render ==========
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #FFE8D6 0%, #DDBEA9 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: 4,
          maxWidth: 450,
          width: '100%',
          borderRadius: 3,
        }}
      >
        {/* 標題 */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#6B705C',
              mb: 1,
            }}
          >
            司機帳號綁定
          </Typography>
          <Typography variant="body2" color="text.secondary">
            首次使用請綁定您的司機帳號
          </Typography>
        </Box>

        {/* LINE 使用者資訊 */}
        {lineUser && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            {lineUser.pictureUrl && (
              <img
                src={lineUser.pictureUrl}
                alt={lineUser.displayName}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                }}
              />
            )}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {lineUser.displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                LINE 帳號
              </Typography>
            </Box>
          </Box>
        )}

        {/* 錯誤訊息 */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* 綁定表單 */}
        <form onSubmit={handleSubmit}>
          {/* 司機編號 */}
          <TextField
            fullWidth
            label="司機編號"
            value={driverId}
            onChange={(e) => {
              setDriverId(e.target.value)
              // 換了代號 → 舊的公司清單不再適用
              setCompanyOptions([])
              setSelectedCompany('')
            }}
            placeholder="請輸入司機編號"
            disabled={loading}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon sx={{ color: '#6B705C' }} />
                </InputAdornment>
              ),
            }}
          />

          {/* 所屬公司（跨公司同代號時才出現） */}
          {companyOptions.length > 1 && (
            <TextField
              select
              fullWidth
              label="所屬公司"
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              disabled={loading}
              sx={{ mb: 2 }}
              helperText="此司機編號存在於多家公司，請選擇您的所屬公司"
            >
              {companyOptions.map((c) => (
                <MenuItem key={c.companyCode} value={c.companyCode}>
                  {c.companyName}（{c.companyCode}）
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* 驗證碼 */}
          <TextField
            fullWidth
            type={showCode ? 'text' : 'password'}
            label="驗證碼"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder="請輸入驗證碼"
            disabled={loading}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: '#6B705C' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleToggleCode} edge="end">
                    {showCode ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* 綁定按鈕 */}
          <Button
            fullWidth
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            sx={{
              backgroundColor: '#6B705C',
              color: 'white',
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: '#5A5F4A',
              },
              '&:disabled': {
                backgroundColor: '#A5A58D',
              },
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={24} sx={{ color: 'white', mr: 1 }} />
                綁定中...
              </>
            ) : (
              '綁定帳號'
            )}
          </Button>
        </form>

        {/* 說明提示 */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: '#e3f2fd',
            borderRadius: 2,
            border: '1px solid #2196f3',
          }}
        >
          <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
            💡 綁定說明
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            請輸入公司提供的司機編號和驗證碼
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            如有問題請聯繫公司管理人員
          </Typography>
        </Box>

        {/* 注意事項 */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 3, display: 'block', textAlign: 'center' }}
        >
          綁定後，此 LINE 帳號將與司機帳號關聯
        </Typography>
      </Paper>
    </Box>
  )
}

export default BindingPage
